/**
 * Cliente del conversation-service (servicio aparte, default :3010). Autentica con el MISMO
 * `access_token` del panel (Bearer) — el servicio resuelve la sesión contra user_sessions.
 * El WebSocket vive en el mismo host (namespace `conversations`).
 */
import axios from "axios";
import type { ActividadDelAsistente } from "@/pages/conversations/agent-activity";
import { runtimeValue } from "@/config/runtime-config";
import {
  ChannelTypeInfo,
  TIPOS_DE_UN_BACKEND_VIEJO,
  codigoDeError,
  isCloudApi,
  threadIdFor,
  ventanaAbierta,
} from "./channel-types";
import { phoneFromThread } from "./whatsapp-phone";

/**
 * URL del conversation-service. Config de runtime inyectada por nginx (envsubst) al arrancar el
 * contenedor (ver runtime-config.template.js + docker-entrypoint.d/40-runtime-config.sh).
 * Prioridad: runtime > build-time (Vite). Así no depende del build-arg.
 *
 * La lectura (y el tipo del global) vive en @/config/runtime-config: declararlo en dos lados hace
 * que TS intente mergear las interfaces y falle si no coinciden exacto.
 *
 * **Sin fallback**: si no está seteada queda `""`. Antes caía a `http://localhost:3010`, que en un
 * admin servido por HTTPS es mixed content — el browser lo bloquea y Conversaciones muere sin
 * ningún error que apunte a la causa.
 *
 * `""` NO significa "mismo origen": los consumidores tienen que chequear
 * `isConversationsConfigured()` antes de llamar. Sin ese guard, axios con `baseURL: ""` y
 * `io("/conversations")` pegan contra el dominio del admin, que devuelve el HTML del index —
 * y eso es lo que en dev terminaba en pantalla blanca (`fe.some is not a function`).
 */
export const CONVERSATIONS_API_URL =
  runtimeValue("CONVERSATIONS_API_URL") ||
  import.meta.env.VITE_CONVERSATIONS_API_URL ||
  "";

/** `false` si no hay URL configurada: no hay a dónde llamar, no lo intentes. */
export const isConversationsConfigured = (): boolean => CONVERSATIONS_API_URL !== "";

export const getAccessToken = () => localStorage.getItem("access_token") ?? "";

/** Agrega el token a una URL de media (/uploads/file) para poder renderizarla en <img>/<audio>. */
export const mediaSrc = (url?: string | null): string => {
  if (!url) return "";
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}token=${encodeURIComponent(getAccessToken())}`;
};

/**
 * Base de los requests al conversation-service.
 *
 * **Se decide por el dato, no por el modo de build.** Si `/runtime-config.js` inyectó una URL
 * —lo hace nginx al arrancar el contenedor, o sea en cualquier entorno deployado— se va directo al
 * servicio. Si no la inyectó, estamos en el dev server de Vite y se usa su proxy `/conv-api`.
 *
 * Mira el runtime-config y **no** `CONVERSATIONS_API_URL`, que además cae a
 * `import.meta.env.VITE_CONVERSATIONS_API_URL`: esa env tiene que estar seteada en local porque es
 * el destino del proxy de Vite, y usarla acá haría que el browser se saltee el proxy justamente
 * donde lo necesitamos. Son dos consumidores distintos de la misma variable.
 *
 * Antes esto era `import.meta.env.DEV ? "/conv-api" : CONVERSATIONS_API_URL`, y ahí estuvo el bug:
 * el build de la imagen resolvió `DEV` a **true**, así que el bundle deployado salió con
 * `baseURL: "/conv-api"`. Esa ruta no existe en el nginx del admin, cae en el `try_files` del SPA y
 * devuelve **index.html con HTTP 200** — o sea que axios ni siquiera lo ve como error. Ese HTML
 * llegaba como `dataSource` de una Table y antd reventaba con `rawData.some is not a function`:
 * pantalla blanca en Conversaciones y en Canales. En local nunca se reproducía, porque ahí el proxy
 * sí resuelve.
 *
 * La condición vieja dependía de una variable de compilación que ya demostró no ser confiable entre
 * entornos; esta depende de si la config llegó, que es lo que realmente determina a dónde hay que ir.
 */
const HTTP_BASE = runtimeValue("CONVERSATIONS_API_URL") || "/conv-api";

// Caer al proxy es lo normal en el dev server y un error en cualquier entorno deployado: ahí la
// config tiene que llegar por `/runtime-config.js`. Se avisa una vez, al cargar, en lugar de dejar
// que se manifieste como una pantalla en blanco más tarde.
if (HTTP_BASE === "/conv-api") {
  console.warn(
    "[conversations.api] sin URL en /runtime-config.js — se usa el proxy /conv-api del dev server. " +
      "Si esto NO es `vite dev`, revisar que /runtime-config.js esté servido y cargado por index.html.",
  );
}

const client = axios.create({ baseURL: HTTP_BASE });
client.interceptors.request.use((config) => {
  config.headers.Authorization = `Bearer ${getAccessToken()}`;
  return config;
});

/**
 * Garantiza que lo que se devuelve sea una lista.
 *
 * Red de contención del modo de falla que tiró la pantalla: si la base apunta a un lugar que no es
 * el servicio, el nginx del admin responde el `index.html` del SPA con **HTTP 200**, así que axios
 * no lo ve como error y el string HTML llega hasta el `dataSource` de una Table. antd hace
 * `rawData.some(...)` sobre él y muere el render entero.
 *
 * Con esto el consumidor recibe `[]` —listado vacío, pantalla viva— y el motivo real queda en
 * consola, que es lo que faltaba para diagnosticarlo sin tener que bajar el bundle.
 */
function asList<T>(data: unknown, endpoint: string): T[] {
  if (Array.isArray(data)) return data as T[];
  const pista =
    typeof data === "string" && data.trimStart().startsWith("<")
      ? " — parece HTML: la base apunta a una ruta que el nginx del admin resuelve con el index.html"
      : "";
  console.error(
    `[conversations.api] ${endpoint} devolvió ${typeof data} en vez de una lista${pista}`,
    data,
  );
  return [];
}

/**
 * `restricted` no es un sabor de "desconectado": el socket puede estar arriba y aun así no se puede
 * enviar, y —a diferencia de una sesión inválida— re-escanear el QR no lo arregla.
 */
export type ChannelConnectionStatus = "connected" | "disconnected" | "qr_pending" | "restricted";

export interface Channel {
  channelId: string;
  storeCode: string;
  type: string;
  name: string;
  identifier: string;
  isActive: boolean;
  connectionStatus: ChannelConnectionStatus;
  lastConnectedAt: string | null;
  /**
   * #177: hasta cuándo WhatsApp tiene restringida la cuenta de este número (ISO), o `null`.
   * Mientras esté vigente no se puede enviar y **el QR no sirve**: la restricción es de la cuenta.
   */
  restrictedUntil: string | null;
  /** `enforcement_type` de WhatsApp (p.ej. `RESTRICT_ALL_COMPANIONS`). */
  restrictionType: string | null;
  /**
   * Cloud API: el `phone_number_id` de Meta, que pone el onboarding. `null` en un canal QR o en uno
   * cloud todavía sin conectar.
   */
  externalAccountId?: string | null;
  /**
   * Lo no secreto del canal. En la Cloud API: la cuenta (`waba_id`), el número, `verified_name`, el
   * aviso del override de webhooks (`webhook_override`), la salud (`health`), el motivo de una
   * desconexión (`disconnection`) y la confirmación del método de pago (`payment_confirmed_at`).
   */
  config?: Record<string, unknown>;
}

// ── el asistente del canal (change conversaciones-agente-ia, D29) ─────────────────

/** `assist` contesta al operador por notas; `auto`, al cliente, y sólo en la Cloud API (D23). */
export type ChannelAgentMode = "assist" | "auto";

export interface ChannelAgent {
  channelId: string;
  userId: number;
  displayName: string;
  mode: ChannelAgentMode;
  enabled: boolean;
  sendRatePerMin: number;
  debounceSeconds: number;
  /** D58: el seller por el que vende el canal; `null`, por el store. */
  sellerCode?: string | null;
  updatedAt: string;
}

export interface SetChannelAgent {
  userId: number;
  displayName: string;
  mode: ChannelAgentMode;
  enabled: boolean;
  sendRatePerMin?: number;
  debounceSeconds?: number;
  sellerCode?: string | null;
}

// ── templates de WhatsApp (change conversaciones-templates) ───────────────────────

/** El estado de una copia en Meta, como lo dice Meta, más los dos del servicio: `pending` y `error`. */
export type TemplateStatus =
  | "pending"
  | "in_review"
  | "approved"
  | "rejected"
  | "paused"
  | "disabled"
  | "deleted"
  | "error";

/** Una variable del texto: de dónde sale su valor al enviar, y cómo se la nombra al completarla. */
export interface TemplateVariable {
  n: number;
  label: string;
  source: "contact_name" | "manual";
}

/** Lo que una tienda escribió una vez (D2): el texto con `{{1}}`, `{{2}}`… y, si quiere, `{tienda}`. */
export interface TemplateDefinition {
  definitionId: string;
  name: string;
  version: number;
  language: string;
  /** La pedida: `UTILITY`. La real está en cada publicación. */
  category: string;
  body: string;
  footer: string | null;
  variables: TemplateVariable[];
  examples: string[];
  /** Del catálogo de xcons: se usa, y editarla crea la versión de la tienda, que la reemplaza. */
  catalog: boolean;
  /** De la tienda, con el nombre de una del catálogo: la reemplaza en sus canales. Si se elimina, vuelve la del catálogo. */
  fromCatalog: boolean;
  createdAt: string;
  retiredAt: string | null;
}

/** La copia de una definición en la cuenta de un canal, con el estado y la categoría que Meta le dio. */
export interface TemplatePublication {
  publicationId: string;
  definitionId: string;
  channelId: string;
  wabaId: string;
  /** El nombre en Meta: `${name}_v${version}`. */
  metaName: string;
  language: string;
  metaId: string | null;
  status: TemplateStatus;
  /** La que decidió Meta: `UTILITY`, `MARKETING`… */
  category: string | null;
  quality: string | null;
  /** El motivo de un rechazo o una pausa, legible. */
  reason: string | null;
  /** El texto con `{tienda}` ya reemplazado: lo que se manda. */
  renderedBody: string;
  renderedFooter: string | null;
  error: string | null;
  publishedAt: string | null;
  lastEventAt: string | null;
  deletedAt: string | null;
  createdAt: string;
}

export interface TemplateItem {
  definition: TemplateDefinition;
  /** La copia en este canal; `null` si todavía no se publicó acá. */
  publication: TemplatePublication | null;
  otherChannels: { channelId: string; name: string; status: TemplateStatus | null }[];
}

export interface TemplatesView {
  channelId: string;
  wabaId: string | null;
  connected: boolean;
  autopublish: boolean;
  items: TemplateItem[];
}

/** El texto de una versión: lo que se escribe en el alta y en una versión nueva. */
export interface TemplateText {
  body: string;
  footer?: string | null;
  examples: string[];
  variables?: TemplateVariable[];
}

/** Con qué template salió un mensaje (D7): el texto ya está renderizado en `content`. */
export interface MessageTemplateRef {
  publicationId: string;
  definitionId: string;
  name: string;
  language: string;
  params: string[];
}

/** Lo que Meta cobró por un saliente, según su acuse (D13). */
export interface MessagePricing {
  category: string | null;
  billable: boolean | null;
}

/** Lo que el backend devuelve de la conexión con Meta (D29). */
export interface CloudConfig {
  appId: string | null;
  configId: string | null;
  graphVersion: string;
}

export interface ConversationListItem {
  conversation_id: string;
  channel_id: string;
  contact_id: number | null;
  contact_name: string | null;
  contact_company: string | null;
  external_thread_id: string;
  status: "open" | "closed";
  owner_user_id: number | null;
  last_message_text: string | null;
  last_message_at: string | null;
  last_message_sender: "operator" | "contact" | null;
  unread_count: number;
  // del JOIN con channels
  channel_name: string;
  channel_type: string;
  store_code: string;
  channel_active: boolean;
  channel_connection: ChannelConnectionStatus;
  /**
   * #177: WhatsApp restringió la cuenta del canal (ISO) o `null`. Mientras esté vigente no se
   * puede enviar — y **no** hay que re-vincular con QR: la restricción es de la cuenta, no de la
   * sesión. El backend igual rechaza el envío con 409 `ACCOUNT_RESTRICTED`.
   */
  channel_restricted_until: string | null;
  /** `enforcement_type` de WhatsApp (p.ej. `RESTRICT_ALL_COMPANIONS`). */
  channel_restriction_type: string | null;
  participants?: { userId: number; name: string; storeCode: string; storeName?: string }[];
  /**
   * Cloud API (D27): hasta cuándo se le puede escribir al contacto, 24 h después de su último mensaje
   * en vivo. `null` si el canal no tiene ventana o si el contacto nunca escribió. Lo que se manda desde
   * el celular no la renueva.
   */
  service_window_expires_at?: string | null;
}

export type QuickFilter = "all" | "mine" | "no_operators";

export interface ConversationFilters {
  storeCode?: string;
  channelId?: string;
  filter?: QuickFilter;
  status?: "open" | "closed";
  q?: string;
  company?: string;
  participantUserId?: number;
  dateFrom?: string;
  dateTo?: string;
  limit?: number;
  /** #117: CSV de contactIds (miembros del Contact group elegido). */
  contactIds?: string;
}

/**
 * Tamaño de página del "cargar más" del chat (015 F5): 10, tanto para la DB como para el pedido a
 * WhatsApp. Se usa también para saber si la página vino incompleta ⇒ la DB se agotó.
 */
export const HISTORY_PAGE = 10;

/**
 * Tope de tamaño de la media, espejo del `MEDIA_MAX_MB` del conversation-service (default 25 MB).
 * Se valida en el front para avisarle al operador al instante, en vez de subir el archivo y comerse
 * un 413 al final. El backend igual lo valida: esto es UX, no la defensa.
 */
export const MEDIA_MAX_MB = 25;
export const MEDIA_MAX_BYTES = MEDIA_MAX_MB * 1024 * 1024;

export const conversationsApi = {
  /**
   * Canales por los que el operador puede **escribir** — los del modal de "Nuevo mensaje".
   *
   * `listChannels` no sirve para eso: es el listado del ABM, que exige admin del Store y trae todos
   * los canales, incluidos aquellos donde el operador tiene `no_access`.
   */
  listWritableChannels: (storeCode?: string) =>
    client
      .get<Channel[]>("/channels/writable", { params: storeCode ? { storeCode } : {} })
      .then((r) => asList<Channel>(r.data, "/channels/writable")),

  /**
   * Canales del store con el nivel de ESE operador (N1). La transpuesta de la grilla del canal:
   * misma tabla, se entra por el operador. Sparse — un canal sin grant viene en `no_access`.
   */
  listOperatorChannelGrants: (storeCode: string, userId: number) =>
    client
      .get<
        Array<{
          channelId: string;
          name: string;
          identifier: string;
          type: string;
          level: ChannelPermissionLevel;
        }>
      >(`/channels/operator/${userId}/grants`, { params: { storeCode } })
      .then((r) => r.data),

  setOperatorChannelGrants: (
    storeCode: string,
    userId: number,
    entries: { channelId: string; level: ChannelPermissionLevel }[],
  ) =>
    client
      .put(`/channels/operator/${userId}/grants`, { entries }, { params: { storeCode } })
      .then((r) => r.data),

  /** Igual, para un seller como entidad: sus operadores heredan el nivel read-only. */
  listSellerChannelGrants: (storeCode: string, sellerCode: string) =>
    client
      .get<
        Array<{
          channelId: string;
          name: string;
          identifier: string;
          type: string;
          level: ChannelPermissionLevel;
        }>
      >(`/channels/seller/${encodeURIComponent(sellerCode)}/grants`, { params: { storeCode } })
      .then((r) => r.data),

  setSellerChannelGrants: (
    storeCode: string,
    sellerCode: string,
    entries: { channelId: string; level: ChannelPermissionLevel }[],
  ) =>
    client
      .put(`/channels/seller/${encodeURIComponent(sellerCode)}/grants`, { entries }, {
        params: { storeCode },
      })
      .then((r) => r.data),

  /** Saca al operador como participante de las conversaciones del store. No toca sus grants. */
  removeOperatorParticipations: (storeCode: string, userId: number) =>
    client
      .delete<{ deleted: number }>(`/channels/operator/${userId}/participations`, {
        params: { storeCode },
      })
      .then((r) => r.data),

  listChannels: (storeCode?: string) =>
    client
      .get<Channel[]>("/channels", { params: storeCode ? { storeCode } : {} })
      .then((r) => asList<Channel>(r.data, "/channels")),

  createChannel: (body: { storeCode: string; type: string; name?: string; identifier?: string }) =>
    client.post<Channel>("/channels", body).then((r) => r.data),

  getChannel: (channelId: string) =>
    client.get<Channel>(`/channels/${channelId}`).then((r) => r.data),

  updateChannel: (
    channelId: string,
    body: { name?: string; identifier?: string; isActive?: boolean; config?: Record<string, unknown> },
  ) => client.patch<Channel>(`/channels/${channelId}`, body).then((r) => r.data),

  /**
   * Los tipos que este ambiente puede dar de alta, con sus capacidades (D26). Un backend que todavía no
   * tiene el endpoint responde 404: ahí sólo se ofrece el QR, que es lo que sabe crear.
   */
  channelTypes: () =>
    client
      .get<ChannelTypeInfo[]>("/channels/types")
      .then((r) => asList<ChannelTypeInfo>(r.data, "/channels/types"))
      .catch((e) => {
        if (e?.response?.status === 404) return TIPOS_DE_UN_BACKEND_VIEJO;
        throw e;
      }),

  /** El asistente del canal, o `null` si no tiene. Alcanza con `viewer`. */
  getChannelAgent: (channelId: string) =>
    client.get<ChannelAgent | null>(`/channels/${channelId}/agent`).then((r) => r.data ?? null),

  /** Crea o reemplaza el asistente del canal. Sólo el admin del Store. */
  setChannelAgent: (channelId: string, body: SetChannelAgent) =>
    client.put<ChannelAgent>(`/channels/${channelId}/agent`, body).then((r) => r.data),

  removeChannelAgent: (channelId: string) =>
    client.delete(`/channels/${channelId}/agent`).then((r) => r.data),

  /** La app de Meta y la configuration de Embedded Signup, para abrir el flujo (D26). */
  cloudConfig: () => client.get<CloudConfig>("/channels/cloud/config").then((r) => r.data),

  /** Conecta el número de la Business App con el `code` del Embedded Signup (D29). Vive 30 s. */
  embeddedSignup: (
    channelId: string,
    body: { code: string; wabaId?: string; phoneNumberId?: string; isCoexistence?: boolean },
  ) => client.post<Channel>(`/channels/${channelId}/cloud/embedded-signup`, body).then((r) => r.data),

  /**
   * El admin confirma que la cuenta de WhatsApp del negocio tiene un método de pago (D2). Queda con quién
   * y para qué cuenta, y limpia el cartel de un `131042` anterior.
   */
  confirmPayment: (channelId: string) =>
    client.post<Channel>(`/channels/${channelId}/cloud/payment-confirmation`).then((r) => r.data),

  /**
   * La conversación de un canal de la Cloud API con ese teléfono, si tiene la ventana de 24 h abierta
   * (D27). Es lo que decide si los selectores de canal ofrecen el canal para ese número: por la Cloud API
   * no se le puede escribir primero a nadie.
   *
   * Es el mismo `POST /conversations` del alta, que en un canal cloud **nunca crea**: busca por la clave
   * canónica del teléfono —la que junta `5411…` con `54911…`— y por sus alias, o responde 409 con el
   * código de la ventana si ese número nunca escribió. Así el front no reimplementa la identidad (D20).
   * En un canal QR ese POST sí crea la conversación: por eso recibe el canal y no sólo su id.
   */
  conversacionConVentana: async (channel: Channel, phone: string) => {
    if (!isCloudApi(channel.type) || phoneFromThread(phone).length < 6) return null;
    try {
      const conv = await client
        .post<ConversationListItem>("/conversations", {
          channelId: channel.channelId,
          externalThreadId: threadIdFor(channel.type, phone),
        })
        .then((r) => r.data);
      return ventanaAbierta(channel.type, conv.service_window_expires_at) ? conv : null;
    } catch (e) {
      if (codigoDeError(e) === "OUTSIDE_SERVICE_WINDOW") return null;
      throw e;
    }
  },

  deleteChannel: (channelId: string) =>
    client.delete(`/channels/${channelId}`).then((r) => r.data),

  startQr: (channelId: string) =>
    client.post<{ status: string }>(`/channels/${channelId}/qr/start`).then((r) => r.data),

  disconnect: (channelId: string) =>
    client.post<{ status: string }>(`/channels/${channelId}/disconnect`).then((r) => r.data),

  listConversations: (filters: ConversationFilters = {}) => {
    const params: Record<string, unknown> = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") params[k] = v;
    });
    return client
      .get<{ items: ConversationListItem[] }>("/conversations", { params })
      .then((r) => r.data.items);
  },

  /**
   * `viaTemplate` (D14): en un canal de la Cloud API crea la conversación aunque no haya ventana, para
   * mandarle un template a un contacto con ficha. Sin él, un canal cloud nunca crea.
   */
  createConversation: (body: { channelId: string; externalThreadId: string; contactId?: number; viaTemplate?: boolean }) =>
    client.post<ConversationListItem>("/conversations", body).then((r) => r.data),

  patchStatus: (conversationId: string, status: "open" | "closed") =>
    client.patch(`/conversations/${conversationId}`, { status }).then((r) => r.data),

  markRead: (conversationId: string, lastMessageId?: string) =>
    client.post(`/conversations/${conversationId}/read`, { lastMessageId }).then((r) => r.data),

  /**
   * Reintenta un saliente que quedó en `failed`, sobre la misma fila: no crea un mensaje nuevo.
   * El backend rechaza el reintento si el mensaje no está fallido — mandarlo dos veces es peor.
   */
  /**
   * Reacciona a un mensaje. **`emoji` vacío quita la reacción propia** — WhatsApp no tiene un
   * método aparte para eso.
   */
  reactToMessage: (conversationId: string, messageId: string, emoji: string) =>
    client
      .post(`/conversations/${conversationId}/messages/${messageId}/reaction`, { emoji })
      .then((r) => r.data),

  retryMessage: (conversationId: string, messageId: string) =>
    client
      .post(`/conversations/${conversationId}/messages/${messageId}/retry`)
      .then((r) => r.data),

  markUnread: (conversationId: string) =>
    client.post(`/conversations/${conversationId}/unread`).then((r) => r.data),

  /**
   * Página del timeline. `before` = cursor (el `occurredAt` del más viejo que ya tenés) y `limit` la
   * cantidad. Sin `before` trae la última página — es lo que usa la carga inicial del chat.
   */
  getTimeline: (conversationId: string, before?: string, limit?: number) =>
    client
      .get<TimelineRow[]>(`/conversations/${conversationId}/timeline`, {
        params: { ...(before ? { before } : {}), ...(limit ? { limit } : {}) },
      })
      .then((r) => asList<TimelineRow>(r.data, "/conversations/:id/timeline")),

  /**
   * "Cargar más" cuando la DB se agotó (015 F5): pide historial a WhatsApp. **No devuelve los
   * mensajes** — llegan por WS (`message:history`), y el cierre por `history:done`.
   * `exhausted: true` = no hay desde dónde pedir (o el provider no pudo).
   */
  fetchHistory: (conversationId: string, count = HISTORY_PAGE) =>
    client
      .post<{ requested: boolean; exhausted: boolean }>(
        `/conversations/${conversationId}/history/fetch`,
        { count },
      )
      .then((r) => r.data),

  sendMessage: (conversationId: string, content: string) =>
    client
      .post(`/conversations/${conversationId}/messages`, {
        contentType: "text",
        content,
        clientRequestId: crypto.randomUUID(),
      })
      .then((r) => r.data),

  /**
   * Manda un template aprobado (D13): lo único que sale fuera de la ventana de 24 h. Un valor por
   * variable, en orden. Vuelve el mensaje, como `sendMessage`.
   */
  sendTemplateMessage: (conversationId: string, publicationId: string, params: string[]) =>
    client
      .post(`/conversations/${conversationId}/template-messages`, {
        publicationId,
        params,
        clientRequestId: crypto.randomUUID(),
      })
      .then((r) => r.data),

  // ── templates de WhatsApp de un canal de la Cloud API ──────────────────────────
  /** Los templates de la tienda, con el estado de la copia de este canal. 404 en un backend sin templates. */
  listTemplates: (channelId: string) =>
    client.get<TemplatesView>(`/channels/${channelId}/cloud/templates`).then((r) => r.data),

  createTemplate: (channelId: string, body: TemplateText & { name: string }) =>
    client.post<TemplatesView>(`/channels/${channelId}/cloud/templates`, body).then((r) => r.data),

  versionTemplate: (channelId: string, definitionId: string, body: TemplateText) =>
    client.post<TemplatesView>(`/channels/${channelId}/cloud/templates/${definitionId}/versions`, body).then((r) => r.data),

  retireTemplate: (channelId: string, definitionId: string) =>
    client.delete<TemplatesView>(`/channels/${channelId}/cloud/templates/${definitionId}`).then((r) => r.data),

  republishTemplate: (channelId: string, publicationId: string) =>
    client
      .post<TemplatesView>(`/channels/${channelId}/cloud/templates/publications/${publicationId}/republish`)
      .then((r) => r.data),

  /** Le pregunta a Meta por las copias de la cuenta: para cuando los webhooks no llegan (D12). */
  refreshTemplates: (channelId: string) =>
    client.post<TemplatesView>(`/channels/${channelId}/cloud/templates/refresh`).then((r) => r.data),

  /** Sube un archivo (presigned → PUT) y devuelve la URL pública + mime. */
  uploadMedia: async (conversationId: string, file: File) => {
    if (file.size > MEDIA_MAX_BYTES) {
      throw new Error(`El archivo supera el límite de ${MEDIA_MAX_MB} MB`);
    }
    const ext = file.name.split(".").pop() || "bin";
    const mime = file.type || "application/octet-stream";
    const target = await client
      .put<{ storageKey: string; uploadUrl: string }>("/uploads/upload-target", {
        conversationId,
        ext,
        contentType: mime,
        // Declarar el peso hace que el backend rechace ANTES de que suban los bytes.
        size: file.size,
      })
      .then((r) => r.data);
    await client.put(target.uploadUrl, file, { headers: { "Content-Type": mime } });
    return {
      mediaUrl: `${CONVERSATIONS_API_URL}/uploads/file?key=${encodeURIComponent(target.storageKey)}`,
      mediaMimeType: mime,
      contentType: mimeToContentType(mime),
    };
  },

  sendMediaMessage: (
    conversationId: string,
    body: { contentType: string; mediaUrl: string; mediaMimeType: string; content?: string },
  ) =>
    client
      .post(`/conversations/${conversationId}/messages`, { ...body, clientRequestId: crypto.randomUUID() })
      .then((r) => r.data),

  /** Detalle de una conversación con el shape del listado (abrir desde notificación). */
  getConversation: (conversationId: string) =>
    client.get<ConversationListItem>(`/conversations/${conversationId}`).then((r) => r.data),

  // ── notas internas (OpenSpec 002) ──────────────────────────────────────
  /** Sesión actual (userId) — para saber si soy el autor de una nota. */
  me: () =>
    client
      .get<{ userId: number; storeCodes: string[]; sellerCodes: string[] }>("/session/me")
      .then((r) => r.data),

  /** Nivel efectivo del operador sobre el canal (gatea crear notas al Editor). */
  myChannelLevel: (channelId: string) =>
    client
      .get<{ channelId: string; level: ChannelPermissionLevel }>(`/channels/${channelId}/my-level`)
      .then((r) => r.data),

  /** Crea nota interna. Sin messageId = general (timeline); con messageId = anidada. */
  /** D56: lo que el asistente está haciendo ahora en la conversación, para el que abre o recarga. */
  getAgentActivity: (conversationId: string) =>
    client
      .get<{ conversationId: string; items: ActividadDelAsistente[] }>(`/conversations/${conversationId}/agent-activity`)
      .then((r) => r.data),

  /** D57: pide la transcripción de un audio. El texto llega por `message:transcript`. */
  requestTranscription: (conversationId: string, messageId: string) =>
    client
      .post<{ messageId: string; transcript: string | null; transcriptStatus: "pending" | "done" | "failed" | null }>(
        `/conversations/${conversationId}/messages/${messageId}/transcription`,
      )
      .then((r) => r.data),

  createNote: (conversationId: string, content: string, messageId?: string) =>
    client
      .post<NoteView>(`/conversations/${conversationId}/notes`, { content, messageId })
      .then((r) => r.data),

  /**
   * Elige una opción de la nota del asistente (D49). Devuelve la nota con la elección y la nota que
   * sale a nombre del operador arrobando al asistente. 409 `ALREADY_ANSWERED` si otro eligió antes:
   * trae la nota actual en `note`.
   */
  chooseNoteOption: (conversationId: string, noteId: string, optionId: string) =>
    client
      .post<{ note: NoteView; reply: NoteView }>(`/conversations/${conversationId}/notes/${noteId}/choice`, {
        optionId,
      })
      .then((r) => r.data),

  deleteNote: (conversationId: string, noteId: string) =>
    client.delete(`/conversations/${conversationId}/notes/${noteId}`).then((r) => r.data),

  // ── mensajes programados (OpenSpec 003) ─────────────────────────────────
  createScheduled: (conversationId: string, content: string, scheduledAt: string) =>
    client
      .post<ScheduledMessage>(`/conversations/${conversationId}/scheduled`, { content, scheduledAt })
      .then((r) => r.data),

  listScheduled: (conversationId: string, status?: string) =>
    client
      .get<ScheduledMessage[]>(`/conversations/${conversationId}/scheduled`, {
        params: status ? { status } : {},
      })
      .then((r) => asList<ScheduledMessage>(r.data, "/conversations/:id/scheduled")),

  cancelScheduled: (conversationId: string, scheduledId: string) =>
    client.delete(`/conversations/${conversationId}/scheduled/${scheduledId}`).then((r) => r.data),

  resendScheduled: (conversationId: string, scheduledId: string) =>
    client
      .post(`/conversations/${conversationId}/scheduled/${scheduledId}/resend`)
      .then((r) => r.data),

  /** Candidatos al autocomplete @ (operadores con acceso ≥ viewer al canal). */
  mentionables: (channelId: string) =>
    client
      .get<{ userId: number; name: string; email?: string }[]>(`/channels/${channelId}/mentionables`)
      .then((r) => r.data),

  // ── notificaciones in-app (OpenSpec 002/010) ────────────────────────────
  listNotifications: (opts: { unreadOnly?: boolean; storeCode?: string } = {}) =>
    client
      .get<AppNotification[]>("/notifications", {
        params: {
          ...(opts.unreadOnly ? { unread: true } : {}),
          ...(opts.storeCode ? { store: opts.storeCode } : {}),
        },
      })
      .then((r) => asList<AppNotification>(r.data, "/notifications")),

  /**
   * Conversaciones con algo sin leer, para el badge del menu (022 C7).
   *
   * Sin `storeCode`: cuenta todos los stores accesibles, igual que la campanita. El sider no conoce
   * —ni deberia— el filtro de la pantalla de Conversaciones.
   */
  unreadConversations: () =>
    client.get<{ unread: number }>("/conversations/unread-count").then((r) => r.data.unread),

  unreadNotifications: (storeCode?: string) =>
    client
      .get<{ unread: number }>("/notifications/unread-count", {
        params: storeCode ? { store: storeCode } : {},
      })
      .then((r) => r.data.unread),

  markNotificationRead: (notificationId: string) =>
    client.post<{ unread: number }>(`/notifications/${notificationId}/read`).then((r) => r.data),

  markNotificationUnread: (notificationId: string) =>
    client.post<{ unread: number }>(`/notifications/${notificationId}/unread`).then((r) => r.data),

  markAllNotificationsRead: () =>
    client.post<{ unread: number }>("/notifications/read-all").then((r) => r.data),

  dismissNotification: (notificationId: string) =>
    client.delete<{ unread: number }>(`/notifications/${notificationId}`).then((r) => r.data),

  // ── permisos de canal (grants) ──────────────────────────────────────────
  getOperatorPermissions: (channelId: string) =>
    client
      .get<OperatorGrant[]>(`/channels/${channelId}/operator-permissions`)
      .then((r) => asList<OperatorGrant>(r.data, "/channels/:id/operator-permissions")),

  setOperatorPermissions: (channelId: string, entries: { userId: number; level: ChannelPermissionLevel }[]) =>
    client.put(`/channels/${channelId}/operator-permissions`, { entries }).then((r) => r.data),

  getSellerPermissions: (channelId: string) =>
    client
      .get<SellerGrant[]>(`/channels/${channelId}/seller-permissions`)
      .then((r) => asList<SellerGrant>(r.data, "/channels/:id/seller-permissions")),

  setSellerPermissions: (channelId: string, entries: { sellerCode: string; level: ChannelPermissionLevel }[]) =>
    client.put(`/channels/${channelId}/seller-permissions`, { entries }).then((r) => r.data),
};

/** Nivel de permiso por canal (sparse: la ausencia ⇒ no_access). */
export type ChannelPermissionLevel = "no_access" | "viewer" | "editor";

export interface OperatorGrant {
  channelId: string;
  userId: number;
  permissionLevel: ChannelPermissionLevel;
}

export interface SellerGrant {
  channelId: string;
  sellerCode: string;
  permissionLevel: ChannelPermissionLevel;
}

/** Mensaje programado (OpenSpec 003). Solo texto en V1. */
export interface ScheduledMessage {
  scheduledId: string;
  conversationId: string;
  channelId: string;
  content: string;
  createdBy: number;
  createdByName: string;
  scheduledAt: string;
  status: "pending" | "sent" | "cancelled" | "failed";
  attempts: number;
  lastError: string | null;
  sentMessageId: string | null;
  createdAt: string;
}

/** Notificación in-app (OpenSpec 002 + scheduled_failed del 003). */
export type NotificationModule = "contact" | "quote" | "order" | "conversation";

export interface AppNotification {
  notificationId: string;
  userId: number;
  type: string;
  module: NotificationModule;
  entityId: string | null;
  entityName: string | null;
  channelName: string | null;
  storeCode: string | null;
  conversationId: string | null;
  channelId: string | null;
  noteId: string | null;
  actorUserId: number;
  actorName: string;
  preview: string;
  readAt: string | null;
  dismissedAt: string | null;
  createdAt: string;
}

/** Una opción de una nota del asistente (D49 de `conversaciones-agente-ia`). */
export interface NoteOption {
  id: string;
  label: string;
  detail: string | null;
}

/** Las opciones de la nota y, si alguien ya eligió, cuál. Se elige una sola vez. */
export interface NoteChoices {
  options: NoteOption[];
  answer: { optionId: string; label: string; userId: number; userName: string; answeredAt: string } | null;
}

/** Nota interna (OpenSpec 002). El contacto nunca la ve. messageId=null ⇒ nota general. */
export interface NoteView {
  noteId: string;
  conversationId?: string;
  messageId?: string | null;
  authorUserId: number;
  authorName: string;
  content: string;
  mentions: { userId: number; name: string }[];
  createdAt?: string;
  /** Sólo en una nota del asistente que pide una decisión (D49). */
  choices?: NoteChoices | null;
}

export interface TimelineRow {
  kind: "message" | "event" | "note";
  id: string;
  occurredAt: string;
  payload: {
    messageId?: string;
    /**
     * Id del proveedor (el de WhatsApp). Segunda llave para encontrar una fila.
     *
     * Hace falta porque un mensaje que entra **en vivo** por socket se agrega al timeline antes de
     * que nadie relea la base, y hasta ahora esa fila no guardaba ningún id con el que volver a
     * encontrarla: `message:repaired` no podía reemplazar la burbuja del `[mensaje en camino…]` y
     * el placeholder quedaba en pantalla hasta el próximo refetch.
     *
     * No viene en el timeline de la base (ahí alcanza `messageId`); sí en los eventos de socket.
     */
    providerMessageId?: string;
    senderType?: "operator" | "contact";
    senderName?: string;
    /**
     * Id del operador que envió. Es lo que permite resolver su nombre y su ficha **al mostrar**, en
     * vez de confiar en `senderName`, que se copió del token al enviar y quedó inconsistente (el
     * mismo operador figura como "Ventas" y como "user:5").
     *
     * `0` en los mensajes enviados desde el teléfono ("Celular"): ahí no hay operador que resolver.
     */
    senderId?: number;
    contentType?: string;
    content?: string | null;
    mediaUrl?: string | null;
    status?: string;
    /**
     * Cloud API (D10): el mensaje se borró para todos, del lado del contacto o desde la App. La fila
     * queda en el hilo; el panel muestra que se eliminó.
     */
    deletedAt?: string | null;
    /** D7: el mensaje salió por un template; el texto ya está en `content`. */
    template?: MessageTemplateRef | null;
    /** D13: lo que Meta cobró, según el acuse. */
    pricing?: MessagePricing | null;
    /** D39/D57: la transcripción de un audio y su estado. `null` = nadie la pidió. */
    transcript?: string | null;
    transcriptStatus?: "pending" | "done" | "failed" | null;
    eventType?: string;
    /** `system` en lo que no hizo nadie: por ejemplo, una conversación importada del celular (D23). */
    actorType?: "operator" | "contact" | "system";
    actorName?: string;
    /** kind='note' (nota general) */
    noteId?: string;
    authorUserId?: number;
    authorName?: string;
    mentions?: { userId: number; name: string }[];
    /** kind='note': las opciones de una nota del asistente (D49). */
    choices?: NoteChoices | null;
    /** kind='message': notas anidadas en la burbuja */
    notes?: NoteView[];
    /**
     * kind='message': reacciones al mensaje (030 F2).
     *
     * Van pegadas a la burbuja, no como una fila del timeline: una reacción no es un mensaje, es un
     * emoji colgado de otro. Viene el set COMPLETO — el evento de tiempo real manda lo mismo, así
     * que el último que llega gana y dos clientes no pueden divergir.
     */
    reactions?: MessageReaction[];
  };
}

/** Una reacción a un mensaje: quién y qué emoji. */
export interface MessageReaction {
  reactorType: "contact" | "operator";
  reactorId: number;
  reactorName: string;
  emoji: string;
}

/** mime → contentType del dominio (text/image/video/audio/document). */
export function mimeToContentType(mime: string): string {
  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("video/")) return "video";
  if (mime.startsWith("audio/")) return "audio";
  return "document";
}
